// P&L rebuild, step 3: pure helpers for building entries in the new storage (crmPnlEntries).
// No imports here, so it can be tested alone (node src/crm/pnlEntry.check.js).

export const BANK_MODES = ["NEFT", "UPI"];            // money moving through the one bank account
export const PAID_FROM_BANK = "Bank";
export const PAID_FROM_OWNER = "Owner";                // Owner paid from his own pocket
export const PAID_FROM_CREDIT = "Credit";              // paid from the prepaid Google credit (Google cost only)
export const GOOGLE_CATEGORY = "Google Cloud";
export const TOPUP_TYPE = "GoogleCredit";              // form type: money paid to Google in advance
export const TOPUP_CATEGORY = "Google credit top-up";  // stored as a Transfer with this category
export const GOOGLE_SOURCE = "google-cost";            // entries booked from Google's own cost figures

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const clean0 = (n) => (n === 0 ? 0 : n); // no "-0"

// Works out the amount without GST and the GST amount from what the person typed.
export function splitGst(typed, rate, included) {
  const total = Number(typed);
  const r = Number(rate) || 0;
  if (!Number.isFinite(total) || total <= 0) return { base: 0, gst: 0 };
  if (r === 0) return { base: total, gst: 0 };
  if (included === "yes") {
    const base = r2(total / (1 + r / 100));
    return { base, gst: r2(total - base) };
  }
  return { base: total, gst: r2((total * r) / 100) };
}

// Fields every new entry carries (the creator fields are added by crmApi).
function common(f, over) {
  return {
    date: f.date,
    description: String(f.description || "").trim(),
    type: f.type,
    category: "",
    party: String(f.party || "").trim(),
    paymentMode: "",
    direction: "",
    amount: 0,
    gstRate: 0,
    gstAmount: 0,
    tdsAmount: 0,
    project: "",
    paidFrom: "",
    source: "manual",
    ...over,
  };
}

// One typed entry -> { error } or { entries: [data, ...] }.
// Normal entries give one data object; Google Cloud (Expense) gives one per Google project.
// f = form fields, links = Google project links from Settings, visibleProjects = project names.
export function buildEntries(f, links, visibleProjects) {
  if (!String(f.description || "").trim()) return { error: "Please enter a description." };
  if (!f.date) return { error: "Please choose a date." };
  // Money paid to Google in advance (prepaid credit). Not an expense: it only moves money.
  if (f.type === TOPUP_TYPE) {
    const amount = Number(f.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { error: "Amount must be more than zero." };
    const fromBank = f.paidFrom === PAID_FROM_BANK;
    if (fromBank && !BANK_MODES.includes(f.paymentMode)) return { error: "Choose NEFT or UPI." };
    return { entries: [common(f, {
      type: "Transfer", amount, direction: "In", category: TOPUP_CATEGORY,
      paymentMode: fromBank ? f.paymentMode : "", paidFrom: fromBank ? PAID_FROM_BANK : PAID_FROM_OWNER,
    })] };
  }
  const rate = f.gstRate === "" || f.gstRate == null ? 0 : Number(f.gstRate);
  const isTransfer = f.type === "Transfer";
  if (!isTransfer && (!Number.isFinite(rate) || rate < 0 || rate > 100)) return { error: "GST % must be between 0 and 100." };

  // Owner's money in or out: always through the bank, no project.
  if (isTransfer) {
    const amount = Number(f.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { error: "Amount must be more than zero." };
    if (!BANK_MODES.includes(f.paymentMode)) return { error: "Choose NEFT or UPI." };
    const out = f.direction === "Out";
    return { entries: [common(f, {
      amount, direction: out ? "Out" : "In", category: out ? "Owner's withdrawal" : "Owner's money",
      paymentMode: f.paymentMode,
    })] };
  }

  const isExpense = f.type === "Expense";
  let paidFrom = "";
  if (isExpense) {
    paidFrom = f.paidFrom === PAID_FROM_OWNER ? PAID_FROM_OWNER : f.paidFrom === PAID_FROM_CREDIT ? PAID_FROM_CREDIT : PAID_FROM_BANK;
    if (paidFrom === PAID_FROM_CREDIT && f.category !== GOOGLE_CATEGORY) return { error: "Google credit can only pay a Google Cloud expense." };
  }
  // Bank money moves by NEFT or UPI. An expense the Owner paid himself needs no bank mode.
  let paymentMode = "";
  if (!isExpense || paidFrom === PAID_FROM_BANK) { // Owner's pocket and Google credit need no bank mode
    if (!BANK_MODES.includes(f.paymentMode)) return { error: "Choose NEFT or UPI." };
    paymentMode = f.paymentMode;
  }
  const tds = f.type === "Income" && f.tdsAmount !== "" && f.tdsAmount != null ? Number(f.tdsAmount) : 0;
  if (!Number.isFinite(tds) || tds < 0) return { error: "TDS must be zero or more." };

  // Google Cloud bill: one amount box per linked Google project; each becomes its own entry under that app.
  if (isExpense && f.category === GOOGLE_CATEGORY) {
    const rows = (links || []).map((l) => ({ l, typed: Number((f.gcRows || {})[l.googleProject]) })).filter((x) => x.typed > 0);
    if (rows.length === 0) return { error: "Type the amount for at least one Google project." };
    return { entries: rows.map(({ l, typed }) => {
      const { base, gst } = splitGst(typed, rate, f.gstIncluded);
      return common(f, {
        category: GOOGLE_CATEGORY, amount: base, gstRate: rate, gstAmount: gst, paymentMode, paidFrom,
        project: l.app, googleProject: l.googleProject,
        description: String(f.description).trim() + " (" + l.googleProject + ")",
      });
    }) };
  }

  const typedAmount = Number(f.amount);
  if (!Number.isFinite(typedAmount) || typedAmount <= 0) return { error: "Amount must be more than zero." };
  if (!f.project || !(visibleProjects || []).includes(f.project)) return { error: "Choose the project." };
  const { base, gst } = splitGst(typedAmount, rate, f.gstIncluded);
  if (tds > base + gst) return { error: "TDS cannot be more than the total amount." };
  return { entries: [common(f, {
    category: f.category, amount: base, gstRate: rate, gstAmount: gst, tdsAmount: tds,
    paymentMode, paidFrom, project: f.project,
  })] };
}

// The opposite entry that cancels one entry. Nothing is erased.
export function buildReversal(e, today) {
  const n = (v) => clean0(-(Number(v) || 0));
  const out = {
    date: today,
    description: "Cancelled: " + (e.description || ""),
    amount: n(e.amount),
    gstAmount: n(e.gstAmount),
    gstRate: Number(e.gstRate) || 0,
    tdsAmount: n(e.tdsAmount),
    type: e.type,
    category: e.category || "",
    direction: e.direction || "",
    paymentMode: e.paymentMode || "",
    party: e.party || "",
    project: e.project || "",
    paidFrom: e.paidFrom || "",
    source: e.source === GOOGLE_SOURCE ? GOOGLE_SOURCE : "manual",
    reversalOf: e.id,
  };
  if (e.googleProject) out.googleProject = e.googleProject;
  return out;
}

// Google cost still to be booked: Google's cost so far (in rupees, per Google project)
// minus what is already booked (cancelled bookings net out because reversals are negative).
// Only Google projects that are linked to an app can be booked.
export function googleToBook(links, costsInr, entries) {
  const booked = {};
  (entries || []).forEach((e) => {
    if (e.source === GOOGLE_SOURCE && e.googleProject) booked[e.googleProject] = r2((booked[e.googleProject] || 0) + (Number(e.amount) || 0));
  });
  return (links || []).map((l) => {
    const cost = costsInr[l.googleProject];
    const had = booked[l.googleProject] || 0;
    const known = typeof cost === "number" && Number.isFinite(cost);
    return {
      googleProject: l.googleProject, app: l.app, known,
      cost: known ? r2(cost) : 0, booked: had, toBook: known ? clean0(r2(cost - had)) : 0,
    };
  });
}

export function googleEntry(row, paidFrom, today) {
  const owner = paidFrom === PAID_FROM_OWNER;
  return {
    date: today,
    description: "Google Cloud cost - " + row.googleProject,
    type: "Expense", category: GOOGLE_CATEGORY, party: "Google", paymentMode: "", direction: "",
    amount: row.toBook, gstRate: 0, gstAmount: 0, tdsAmount: 0,
    project: row.app, googleProject: row.googleProject,
    paidFrom: owner ? PAID_FROM_OWNER : paidFrom === PAID_FROM_CREDIT ? PAID_FROM_CREDIT : PAID_FROM_BANK, source: GOOGLE_SOURCE,
  };
}

// ---- Edit an entry (step 5) ----
// Only four things can ever be changed on an existing entry: description, customer / vendor name,
// project and category. Amount, date, type, GST, TDS, payment mode and Paid from are locked, so the
// bank balance, GST and TDS figures can never change quietly. If one of those is wrong: cancel the
// entry and add it again. Every change is written to the entry's own change record (editLog, one key per edit).
export const EDIT_FIELD_NAMES = { description: "Description", party: "Customer / vendor", project: "Project", category: "Category" };

// Which boxes may change for this entry? cancelledIds = ids of entries that already have a cancelling entry.
export function editRules(e, cancelledIds) {
  if (!e) return { ok: false, reason: "Entry not found." };
  if (e.reversalOf) return { ok: false, reason: "A cancelling entry cannot be edited." };
  if (cancelledIds && cancelledIds.has(e.id)) return { ok: false, reason: "A cancelled entry cannot be edited." };
  if (!(Number(e.amount) > 0)) return { ok: false, reason: "This entry cannot be edited." };
  // Owner's money, Google credit top-ups and Google cost lines: project and category are fixed by their meaning.
  const locked = e.type === "Transfer" || !!e.googleProject;
  return { ok: true, description: true, party: true, project: !locked, category: !locked };
}

// f = { description, party, project, category } as typed.
// opts = { cancelledIds, visibleProjects, categories (allowed category names), by (e-mail), now (ISO time) }.
// Returns { error } or { update, changes }. "update" holds only the changed boxes plus the longer editLog.
export function buildEdit(e, f, opts) {
  const o = opts || {};
  const rule = editRules(e, o.cancelledIds);
  if (!rule.ok) return { error: rule.reason };
  const next = {
    description: String(f.description == null ? "" : f.description).trim(),
    party: String(f.party == null ? "" : f.party).trim(),
    project: rule.project ? String(f.project == null ? "" : f.project) : String(e.project || ""),
    category: rule.category ? String(f.category == null ? "" : f.category) : String(e.category || ""),
  };
  if (!next.description) return { error: "Please enter a description." };
  if (next.description.length > 300) return { error: "The description is too long (300 letters at most)." };
  if (next.party.length > 200) return { error: "The customer / vendor name is too long." };
  const now = (k) => String(e[k] == null ? "" : e[k]);
  if (next.project !== now("project") && !(o.visibleProjects || []).includes(next.project)) return { error: "Choose the project." };
  if (next.category !== now("category") && !(o.categories || []).includes(next.category)) return { error: "Choose the category." };
  const changes = [];
  ["description", "party", "project", "category"].forEach((k) => {
    if (next[k] !== now(k)) changes.push({ field: k, from: now(k), to: next[k] });
  });
  if (changes.length === 0) return { error: "Nothing was changed." };
  const update = {};
  changes.forEach((c) => { update[c.field] = c.to; });
  // The change record is a map with one key per edit (e0001, e0002, ...). Old keys are copied unchanged.
  const log = { ...(e.editLog && !Array.isArray(e.editLog) ? e.editLog : {}) };
  let n = Object.keys(log).length + 1;
  while (log["e" + String(n).padStart(4, "0")]) n += 1;
  log["e" + String(n).padStart(4, "0")] = { at: o.now || "", by: o.by || "", changes };
  update.editLog = log;
  return { update, changes };
}

// The change record of an entry as a list, oldest first.
export function editLogList(e) {
  const l = e && e.editLog;
  if (!l) return [];
  if (Array.isArray(l)) return l;
  return Object.keys(l).sort().map((k) => l[k]);
}

// One line of words for a list of changes, e.g. "Project: Company (common) → CRM; Category: Other → Legal".
export function describeChanges(changes) {
  const show = (v) => (v === "" || v == null ? "(empty)" : String(v));
  return (changes || []).map((c) => (EDIT_FIELD_NAMES[c.field] || c.field) + ": " + show(c.from) + " → " + show(c.to)).join("; ");
}

// The Today numbers, from ALL entries (reversals carry negative amounts, so cancelled entries net out).
//  bank    = Owner's money in - out + income received (with GST, after TDS) - bank-paid expenses - bank-paid Google top-ups
//  capital = Owner's money in - out + expenses the Owner paid from his pocket + Google top-ups the Owner paid from his pocket
//  credit  = Google top-ups - Google cost paid from the credit
// Expenses with no Paid-from tag are counted nowhere and reported in "untagged".
export function todayNumbers(entries) {
  let ownerIn = 0, ownerOut = 0, incomeCash = 0, expBank = 0, expOwner = 0, expCredit = 0;
  let topBank = 0, topOwner = 0, untagged = 0;
  (entries || []).forEach((e) => {
    const amt = Number(e.amount) || 0;
    const tot = amt + (Number(e.gstAmount) || 0);
    if (e.type === "Transfer") {
      if (e.category === TOPUP_CATEGORY) {
        if (e.paidFrom === PAID_FROM_BANK) topBank += amt; else topOwner += amt;
      } else if (e.direction === "Out") ownerOut += amt;
      else ownerIn += amt;
    } else if (e.type === "Income") {
      incomeCash += tot - (Number(e.tdsAmount) || 0);
    } else if (e.type === "Expense") {
      if (e.paidFrom === PAID_FROM_BANK) expBank += tot;
      else if (e.paidFrom === PAID_FROM_OWNER) expOwner += tot;
      else if (e.paidFrom === PAID_FROM_CREDIT) expCredit += tot;
      else untagged += 1;
    }
  });
  const ownerNet = r2(ownerIn - ownerOut);
  return {
    bank: clean0(r2(ownerNet + incomeCash - expBank - topBank)),
    capital: clean0(r2(ownerNet + expOwner + topOwner)),
    capitalParts: { ownerNet, expOwner: r2(expOwner), topOwner: r2(topOwner) },
    credit: clean0(r2(topBank + topOwner - expCredit)),
    untagged,
  };
}

// Income, expenses and profit for each project (amounts without GST; Owner's money is not counted).
// Visible projects always get a row; any other project (hidden, or an old name) only if it has entries.
// An entry with no project counts under the common project.
export const COMMON_NAME = "Company (common)";
export function projectSummary(entries, visibleNames) {
  const map = {};
  const order = [];
  const row = (n) => {
    if (!map[n]) { map[n] = { project: n, income: 0, expense: 0, google: 0 }; order.push(n); }
    return map[n];
  };
  (visibleNames || []).forEach(row);
  (entries || []).forEach((e) => {
    if (e.type !== "Income" && e.type !== "Expense") return;
    const r = row(e.project || COMMON_NAME);
    const amt = Number(e.amount) || 0;
    if (e.type === "Income") r.income += amt;
    else { r.expense += amt; if (e.category === GOOGLE_CATEGORY) r.google += amt; }
  });
  const rows = order.map((n) => {
    const r = map[n];
    return { project: n, income: r2(r.income), expense: r2(r.expense), google: r2(r.google), profit: clean0(r2(r.income - r.expense)) };
  });
  const total = rows.reduce((s, r) => ({
    income: r2(s.income + r.income), expense: r2(s.expense + r.expense), google: r2(s.google + r.google), profit: clean0(r2(s.profit + r.profit)),
  }), { income: 0, expense: 0, google: 0, profit: 0 });
  return { rows, total };
}
